import { captureException, captureMessage, withScope } from "@sentry/nextjs"

// Expected Stripe card error codes that shouldn't be reported to Sentry
// These are user errors, not system/code bugs
// See: https://stripe.com/docs/declines/codes
const EXPECTED_STRIPE_DECLINE_CODES = new Set([
  "authentication_required",
  "approve_with_id",
  "card_not_supported",
  "card_velocity_exceeded",
  "currency_not_supported",
  "do_not_honor",
  "do_not_try_again",
  "duplicate_transaction",
  "expired_card",
  "incorrect_cvc",
  "incorrect_number",
  "incorrect_zip",
  "insufficient_funds",
  "invalid_account",
  "invalid_amount",
  "invalid_cvc",
  "invalid_expiry_month",
  "invalid_expiry_year",
  "invalid_number",
  "issuer_not_available",
  "lost_card",
  "merchant_blacklist",
  "new_account_information_available",
  "no_action_taken",
  "not_permitted",
  "pickup_card",
  "pin_try_exceeded",
  "processing_error",
  "reenter_transaction",
  "restricted_card",
  "revocation_of_all_authorizations",
  "revocation_of_authorization",
  "security_violation",
  "service_not_allowed",
  "stolen_card",
  "stop_payment_order",
  "testmode_decline",
  "transaction_not_allowed",
  "try_again_later",
  "withdrawal_count_limit_exceeded",
])

const EXPECTED_STRIPE_ERROR_CODES = new Set([
  "card_declined",
  "expired_card",
  "incorrect_cvc",
  "incorrect_number",
  "incorrect_zip",
  "invalid_cvc",
  "invalid_expiry_month",
  "invalid_expiry_year",
  "invalid_number",
  // 3-D Secure failed or was abandoned; Stripe sends it with type invalid_request_error.
  "payment_intent_authentication_failure",
  "postal_code_invalid",
])

const EMAIL_VERIFICATION_REQUIRED_ERROR_CODE = "EMAIL_VERIFICATION_REQUIRED"
const EMAIL_VERIFICATION_REQUIRED_MESSAGE = "Email verification required before purchase."

interface GraphQLErrorExtension {
  errorCode?: string
  headerOverride?: string
}

interface GraphQLErrorDetails {
  extensions?: GraphQLErrorExtension
  message?: string
}

interface GraphQLRequestErrorLike {
  response?: {
    errors?: GraphQLErrorDetails[]
  }
}

/**
 * Check if the error is an expected Stripe card error that users need to handle
 * (not a bug that should be reported to Sentry)
 */
const isExpectedStripeError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false

  const err = error as Record<string, unknown>

  // Check for decline_code (e.g., insufficient_funds)
  if (typeof err.decline_code === "string" && EXPECTED_STRIPE_DECLINE_CODES.has(err.decline_code)) {
    return true
  }

  // Check for error code (e.g., card_declined)
  if (typeof err.code === "string" && EXPECTED_STRIPE_ERROR_CODES.has(err.code)) {
    return true
  }

  // Check for card_error type
  if (err.type === "card_error") {
    return true
  }

  return false
}

// The member's browser can't reach Stripe: Stripe.js didn't load, or a call to Stripe failed.
// Stripe sets api_connection_error as the type, with no code.
const isStripeUnreachable = (error: unknown): boolean => {
  if (error instanceof Error) return error.message === "Failed to load Stripe.js"
  return (
    !!error &&
    typeof error === "object" &&
    (error as { type?: unknown }).type === "api_connection_error"
  )
}

// A Stripe error object without the PaymentIntent's description (it names the member) or the card.
const stripeErrorSummary = (error: Record<string, unknown>) => {
  const paymentIntent = error.payment_intent as { id?: unknown; status?: unknown } | undefined
  return {
    code: error.code,
    decline_code: error.decline_code,
    payment_intent: paymentIntent
      ? { id: paymentIntent.id, status: paymentIntent.status }
      : undefined,
    type: error.type,
  }
}

// graphql-request's messages end with the whole response and request, variables included: what
// the member typed (passwords, login codes, phone numbers). Keep only the part before it.
export const withoutRequest = (text: string) => text.split(': {"response":')[0]

const getFirstGraphQLError = (error: unknown): GraphQLErrorDetails | undefined => {
  if (!error || typeof error !== "object") return undefined

  const err = error as GraphQLRequestErrorLike
  return err.response?.errors?.[0]
}

type ErrorContext = "checkout" | "graphql" | "unknown"

interface CaptureOptions {
  context?: ErrorContext
  extra?: Record<string, unknown>
}

/**
 * Process an exception with proper context and filtering.
 *
 * - Filters out expected Stripe user errors (card declined, insufficient funds, etc.)
 * - Reports Stripe being unreachable as one warning-level issue
 * - Adds meaningful context to errors reported to Sentry
 * - Converts raw objects to proper Error instances with structured extra data
 */
export const processException = (error: unknown, options: CaptureOptions = {}) => {
  const { context = "unknown", extra = {} } = options

  // Always log in development
  if (process.env.NODE_ENV !== "production") {
    console.error(`[${context}]`, error)
    return
  }

  // Don't send expected Stripe errors to Sentry - they're not bugs
  if (isExpectedStripeError(error)) {
    console.warn("[Expected Stripe Error]", stripeErrorSummary(error as Record<string, unknown>))
    return
  }

  if (isStripeUnreachable(error)) {
    withScope((scope) => {
      scope.setFingerprint(["stripe-unreachable"])
      scope.setTag("error_context", context)
      captureMessage("Stripe unreachable", "warning")
    })
    return
  }

  withScope((scope) => {
    scope.setTag("error_context", context)

    // Add any extra data
    Object.entries(extra).forEach(([key, value]) => {
      scope.setExtra(key, value)
    })

    // Handle different error types
    if (error instanceof Error) {
      captureException(error)
    } else if (error && typeof error === "object") {
      // Convert raw object to structured error
      const err = error as Record<string, unknown>
      const message = (err.message as string) || `${context} error`

      scope.setExtra("raw_error", stripeErrorSummary(err))

      // Capture as message with the error code if available
      const errorCode = err.code || err.decline_code || err.type
      const fullMessage = errorCode
        ? `${context}: ${message} (${errorCode})`
        : `${context}: ${message}`

      captureMessage(fullMessage, "error")
    } else {
      captureMessage(`${context}: ${String(error)}`, "error")
    }
  })
}

/**
 * Convenience wrapper for checkout-related errors
 */
export const processCheckoutException = (error: unknown, extra?: Record<string, unknown>) => {
  processException(error, { context: "checkout", extra })
}

/**
 * Convenience wrapper for GraphQL query errors
 */
export const processGraphQLException = (error: unknown, extra?: Record<string, unknown>) => {
  processException(error, { context: "graphql", extra })
}

export const getGraphQLErrorHeaderOverride = (error: unknown): string | undefined => {
  return getFirstGraphQLError(error)?.extensions?.headerOverride
}

export const getGraphQLErrorMessage = (error: unknown): string | undefined => {
  return getFirstGraphQLError(error)?.message
}

// For tracking data: the API's own message, never what the member typed.
export const errorForTracking = (error: unknown): string =>
  getGraphQLErrorMessage(error) ??
  (error instanceof Error ? withoutRequest(error.message) : "Unknown error")

// The SMS limits answer "Too many requests…" and last up to an hour: say so, or members keep
// retrying into them. A number Twilio refuses gets the API's own words.
export const phoneSubmitErrorMessage = (error: unknown): string => {
  const message = getGraphQLErrorMessage(error)
  if (message?.startsWith("Too many requests")) {
    return "Too many codes were requested for this number. Please try again later."
  }
  if (message?.startsWith("This phone number isn't valid")) return message
  return "Something went wrong, please try again!"
}

export const isEmailVerificationRequiredError = (error: unknown): boolean => {
  const graphQLError = getFirstGraphQLError(error)
  return (
    graphQLError?.extensions?.errorCode === EMAIL_VERIFICATION_REQUIRED_ERROR_CODE ||
    graphQLError?.message === EMAIL_VERIFICATION_REQUIRED_MESSAGE
  )
}
